import { create, type StateCreator } from "zustand";

import type { SimplifiedExtractedDocumentResponse } from "Infrastructure/Api/Api";

interface DocumentExtractModalSlice {
	documentExtractModalOpen: boolean;
	documentExtractData: SimplifiedExtractedDocumentResponse | null;
	openDocumentExtractModal: (data: SimplifiedExtractedDocumentResponse) => void;
	closeDocumentExtractModal: () => void;
}

const createDocumentExtractModalSlice: StateCreator<
	LayoutStore,
	[],
	[],
	DocumentExtractModalSlice
> = (set) => ({
	documentExtractModalOpen: false,
	documentExtractData: null,
	openDocumentExtractModal: (data) =>
		set({ documentExtractModalOpen: true, documentExtractData: data }),
	closeDocumentExtractModal: () =>
		set({ documentExtractModalOpen: false, documentExtractData: null }),
});

type JobState =
	| { status: "pending" | "processing"; fileName?: string }
	| { status: "completed"; data: unknown }
	| { status: "failed"; error?: string };

type JobsMap = Record<string, { id: string; notified: boolean } & JobState>;

interface JobsSlice {
	jobs: JobsMap;
	addJob: (
		id: string,
		init?: Partial<JobState> & { fileName?: string },
	) => void;
	completeJob: (id: string, data: unknown) => void;
	failJob: (id: string, error?: string) => void;
	markNotified: (id: string) => void;
	removeJob: (id: string) => void;
}

const createJobsSlice: StateCreator<LayoutStore, [], [], JobsSlice> = (
	set,
	get,
) => ({
	jobs: {},
	addJob: (id, init) =>
		set((s) => ({
			jobs: {
				...s.jobs,
				[id]: {
					id,
					notified: false,
					status: init?.status ?? "pending",
					fileName: init?.fileName,
					...(init ?? {}),
				} as JobsMap[string],
			},
		})),
	completeJob: (id, data) =>
		set((s) => ({
			jobs: { ...s.jobs, [id]: { ...s.jobs[id], status: "completed", data } },
		})),
	failJob: (id, error) =>
		set((s) => ({
			jobs: { ...s.jobs, [id]: { ...s.jobs[id], status: "failed", error } },
		})),
	markNotified: (id) =>
		set((s) => ({
			jobs: { ...s.jobs, [id]: { ...s.jobs[id], notified: true } },
		})),
	removeJob: (id) => {
		const copy = { ...get().jobs };
		delete copy[id];
		set({ jobs: copy });
	},
});

type LayoutStore = DocumentExtractModalSlice & JobsSlice;

export const useLayoutStore = create<LayoutStore>()((...a) => ({
	...createDocumentExtractModalSlice(...a),
	...createJobsSlice(...a),
}));

export const layoutStore = { get: () => useLayoutStore.getState() };
